import Table from '@risksmart-app/components/src/table';
import { ParentTypes as Parent_Type_Enum } from '@risksmart-app/domain/src/types/consts/parent-type';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ExportButton from 'src/components/export-button';
import { useGetTestResults } from 'src/hooks/queries';
import { PageLayout } from 'src/layouts';

import CustomisableRibbon from '@/components/customisable-ribbon/CustomisableRibbon';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { useRibbonAndExport } from '@/hooks/useRibbonAndExport';
import { getCounter } from '@/utils/collectionUtils';

import { ControlRegisterToolbar } from '../ControlRegisterToolbar';
import TestResultModal from '../update/tabs/TestResultModal';
import { useGetCollectionTableProps } from './config';
import type { ControlTestTableFields } from './types';
import { useGetDefaultRibbonFilters } from './useGetDefaultRibbonFilters';

const Page: FC = () => {
  const { t: st } = useTranslation(['common'], { keyPrefix: 'testResults' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('object_toolbar');

  const { data, loading, refetch } = useGetTestResults({ queryArgs: {} });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedTestId, setSelectedTestId] = useState<string | undefined>(
    undefined
  );
  const [selectedTestParentId, setSelectedTestParentId] = useState<
    string | undefined
  >(undefined);

  const handleTestResultModalOpen = (item: ControlTestTableFields) => {
    setIsEditOpen(true);
    setSelectedTestId(item.Id);
    setSelectedTestParentId(item.ParentControlId);
  };
  const handleTestResultModalClose = async (saved?: boolean | undefined) => {
    setIsEditOpen(false);

    if (saved) {
      await refetch();
    }
  };

  const tableProps = useGetCollectionTableProps(
    data?.test_result,
    handleTestResultModalOpen
  );
  // Shared by the legacy PageLayout counter and the object toolbar: the toolbar
  // renders `header.counter` verbatim and does NOT add parentheses (see
  // `ObjectToolbarHeader`), so both paths take the already-formatted `(12)`.
  // `getCounter` returns '' while loading, which the toolbar treats as no count.
  const counter = getCounter(tableProps.totalItemsCount, loading);
  const title = st('register_title');

  const { ribbonProps, ribbonExportProps } = useRibbonAndExport(
    useGetDefaultRibbonFilters
  );

  return (
    <PageLayout
      helpTranslationKey={'testResults.registerHelp'}
      title={title}
      counter={counter}
      // Object-level toolbar (CUS-153) — flag-gated; mounted only when on so
      // `useExportOptions` runs on exactly one path (toolbar vs ExportButton).
      headerOverride={
        objectToolbarEnabled ? (
          <ControlRegisterToolbar
            title={title}
            counter={counter}
            tableProps={tableProps}
            getActiveRibbonFilters={ribbonExportProps.getActiveRibbonFilters}
          />
        ) : undefined
      }
      actions={
        <ExportButton
          tableProps={tableProps}
          entityLabel={title}
          {...ribbonExportProps}
        />
      }
    >
      <CustomisableRibbon
        items={tableProps.allItems}
        loading={loading}
        propertyFilterQuery={tableProps.propertyFilterQuery}
        onFilterQueryChanged={tableProps.actions.setPropertyFiltering}
        filteringProperties={tableProps.filteringProperties}
        filteringOptions={tableProps.propertyFilterProps.filteringOptions}
        parentType={Parent_Type_Enum.TestResult}
        {...ribbonProps}
        relocateEditToToolbar={objectToolbarEnabled}
      />

      <Table {...tableProps} loading={loading} />

      {isEditOpen && selectedTestId && (
        <TestResultModal
          parentControlId={selectedTestParentId || ''}
          Id={selectedTestId}
          onDismiss={handleTestResultModalClose}
          assessmentMode={'rating'}
        />
      )}
    </PageLayout>
  );
};

export default Page;
